/**
 * Partial and semipartial correlation.
 *
 * The partial correlation of x and y controlling for Z = (z₁ … z_k) is the Pearson
 * correlation between the parts of x and y that Z cannot explain: the residuals of x
 * regressed on Z and of y regressed on Z. Those regressions are solved by QR
 * (`linalg.ts`), not by inverting a correlation matrix, so strongly correlated controls
 * do not cost precision.
 *
 * The semipartial (part) correlation removes Z from only one variable — the residual of
 * x on Z correlated with y as it stands — and measures how much of y's *total* variance
 * x uniquely adds. Its square is the increase in R² when x joins Z as a predictor of y.
 *
 * Inference is the same as for any correlation with k extra parameters estimated:
 * t = r√((n − 2 − k) / (1 − r²)) on n − 2 − k degrees of freedom, and a Fisher-z
 * interval with standard error 1/√(n − 3 − k). The tests are pinned to R's
 * `cor`/`solve` (precision-matrix identity) and `pcor.test` formulas.
 */

import { pearson } from "@/lib/stats/descriptive";
import { normalQuantile, studentTTwoTailed } from "@/lib/stats/distributions";
import { qrLeastSquares } from "@/lib/tool-kit/stats-regression/linalg";

export interface PartialReport {
  n: number;
  controls: number;
  df: number;
  /** r(x, y) with nothing controlled. */
  zeroOrder: number;
  partial: number;
  /** r(y, x·Z): Z removed from x only. */
  semipartialX: number;
  /** r(x, y·Z): Z removed from y only. */
  semipartialY: number;
  t: number;
  p: number;
  level: number;
  ci: [number, number] | null;
  /** Zero-order r of each control with x and with y. */
  controlR: { withX: number; withY: number }[];
  /** For one control: the textbook first-order formula's pieces. */
  firstOrder: { rxz: number; ryz: number } | null;
  /** x and y with Z removed — the points of the added-variable plot. */
  residualsX: number[];
  residualsY: number[];
}

function residualsOn(
  controls: readonly (readonly number[])[],
  target: readonly number[]
): number[] {
  const ones = new Array<number>(target.length).fill(1);
  return qrLeastSquares([ones, ...controls], target).residuals;
}

export function fisherInterval(
  r: number,
  standardError: number,
  level: number
): [number, number] | null {
  if (!(Number.isFinite(standardError) && Math.abs(r) < 1)) {
    return null;
  }
  const z = Math.atanh(r);
  const half = normalQuantile(1 - (1 - level) / 2) * standardError;
  return [Math.tanh(z - half), Math.tanh(z + half)];
}

export function partialCorrelation(
  x: readonly number[],
  y: readonly number[],
  controls: readonly (readonly number[])[],
  level = 0.95
): PartialReport | null {
  const n = x.length;
  const k = controls.length;
  const df = n - 2 - k;
  if (y.length !== n || df < 1) {
    return null;
  }
  const ex = residualsOn(controls, x);
  const ey = residualsOn(controls, y);
  const partial = pearson(ex, ey);
  const zeroOrder = pearson(x, y);
  if (Number.isNaN(partial) || Number.isNaN(zeroOrder)) {
    return null;
  }
  const t =
    Math.abs(partial) >= 1
      ? Math.sign(partial) * Number.POSITIVE_INFINITY
      : partial * Math.sqrt(df / (1 - partial * partial));
  const controlR = controls.map((z) => ({
    withX: pearson(z, x),
    withY: pearson(z, y),
  }));
  return {
    n,
    controls: k,
    df,
    zeroOrder,
    partial,
    semipartialX: pearson(y, ex),
    semipartialY: pearson(x, ey),
    t,
    p: Number.isFinite(t) ? studentTTwoTailed(t, df) : 0,
    level,
    ci: fisherInterval(partial, 1 / Math.sqrt(n - 3 - k), level),
    controlR,
    firstOrder:
      k === 1 ? { rxz: controlR[0].withX, ryz: controlR[0].withY } : null,
    residualsX: ex,
    residualsY: ey,
  };
}
